<div
  #tableScrollWrapper
  [class.table-scroll-wrapper]="isScrollable"
  [class.scroll-active]="scrollActive"
>
  <table
    [class]="'table styled-table ' + theme"
    [class.table-fixed]="tableFixed"
    [class.color-print]="colorPrint">
    <thead>
      <!--
        Print-only headings row.
        Adding the headers to the table header allows the print header to print on every page,
        including tables that have rows that expand on multiple pages.
        Rendering it outside of the table (even when fixed to the top left) will not cause the
        header to print on pages where a table's rows span over multiple pages.
       -->
      @if(printableHeaders?.length) {
        <tr class="print-headings-row">
          <th class="fixed-col print-headings-cell" [attr.colspan]="columns.length">
          <zbp-printable-report-heading [headings]="printableHeaders" />
          </th>
        </tr>
      }
      <tr>
        <!-- loop over the columns to generate header -->
        <ng-container *ngFor="let column of columns; trackBy: trackByColumn">
          @let ariaLabelValue = column.ariaLabelTranslationKey
                                      ? (column.ariaLabelTranslationKey | translate)
                                      : column.ariaLabel;
          <th [attr.aria-label]="ariaLabelValue ?? column['header'] ?? column.key"
              [style.text-align]="column.textAlign"
              [style.width]="column.columnWidth"
              [colSpan]="column.columnSpan ?? 1"
              [ngbTooltip]="column.tooltip ? column.tooltip : ''"
              [triggers]="'click hover focus'"
              [container]="'body'"
              [attr.aria-hidden]="column.type === ZbpDataTable.BLANK_COLUMN ? 'true' : 'false'"
              [attr.tabindex]="column.type === ZbpDataTable.SORTABLE_COLUMN ? '-1' : '0'"
              [attr.aria-sort]="column.type === ZbpDataTable.SORTABLE_COLUMN ? getSortAriaState(column) : 'none'"
          >

            <!-- Blank Column Header -->
            <ng-container *ngIf="column.type === ZbpDataTable.BLANK_COLUMN">
              <div class="hidden">
                <!-- put something in there for screen readers and hide using css -->
                {{ ariaLabelValue }}
              </div>
            </ng-container>

            <!-- Simple Column Header -->
            <ng-container *ngIf="column.type === ZbpDataTable.SIMPLE_COLUMN">
              {{ column.headerTranslationKey ? (column.headerTranslationKey | translate) : column.header }}

              @if(column.showTooltipIcon) {
                <i class="fa fa-circle-question d-print-none"></i>
              }
            </ng-container>

            <!-- Sortable Column Header -->
            <ng-container *ngIf="column.type === ZbpDataTable.SORTABLE_COLUMN">
              <button class="sortable-column-header"
                      (click)="onSortColumnClick(column.key)"
                      [attr.aria-label]="column.tooltip ? column.tooltip : column.header"
                      type="button">
                <span>{{ column.header }}</span>
                <i class="fa sort-icon d-print-none"
                  [ngClass]="getSortClass(column.key)"
                ></i>
              </button>
            </ng-container>

            <ng-container *ngIf="column.type === ZbpDataTable.CHECKBOX_COLUMN">
              <input
                 type="checkbox"
                 (click)="toggleAllCheckboxes(column)"
                 [formControl]="_checkAllStateForm"
                [attr.aria-labelledby]="ariaLabelValue ?? column.header"
                />
              {{ column.header ?? '' }}
            </ng-container>

           <!-- Accessible tooltip text for arrow keyboard navigation with screen reader. -->
            @if(column.tooltip) {
              <p class="sr-only">{{column.tooltip}}</p>
            }
          </th>
        </ng-container>
      </tr>
    </thead>
    <tbody>
    <!-- if loading, show spinner -->
    <ng-container *ngIf="!isLoading else spinnerTemplate">

      <!-- if results are missing, show no results message -->
      <ng-container *ngIf="rows.length > 0 else noResultsTemplate">

        <!-- loop over the data rows -->
        <ng-container *ngFor="let row of rows; index as i; trackBy: handleTracking">

          <!-- Tracks which row is expanded/collapsed for nested tables. -->
          @let collapsibleRowId = uniqueIdentifierPrefix + 'collapsible_row_' + i;

          <tr class="styled-row"
              [formGroup]="formArray.at(i) ?? EMPTY_FORM_GROUP"
              [class.disabled]="row.isDisabled"
              [ngbTooltip]="row.tooltip"
              [container]="'body'">

            <!-- for each row loop over the columns -->
            <ng-container *ngFor="let column of columns">

              <!-- get cell from the row based on current column -->
              <ng-container *ngrxLet="getCell(row, column); let cell">

                <!-- Text Cell -->
                <ng-container *ngIf="cell.type === ZbpDataTable.TEXT_CELL">
                  <td id="{{column.key}}_{{i}}"
                      [attr.title]="cell.tooltip"
                      [attr.aria-label]="cell.ariaLabel"
                      class="text-cell"
                      [attr.role]="cell.markRowHeader ? 'rowheader' : 'cell'"
                      [colSpan]="column.columnSpan ?? cell.columnSpan ?? 1"
                      [style.text-align]="cell.textAlign"
                      [style.backgroundColor]="cell.backgroundColor"
                      [style.border-right]="cell.borderRight"
                      [style.border-bottom]="cell.borderBottom"
                      [style.align-content]="cell.contentAlign"
                      [attr.aria-hidden]="column.type === ZbpDataTable.BLANK_COLUMN ? 'true' : 'false'"
                      [style.font-weight]="cell.fontWeight ?? 500"
                    >
                    {{ cell.text }}
                  </td>
                </ng-container>

                <!-- Html Cell -->
                <ng-container *ngIf="cell.type === ZbpDataTable.HTML_CELL">
                  <td id="{{column.key}}_{{i}}"
                      [ngbTooltip]="cell.tooltip ? cell.tooltip : ''"
                      #t="ngbTooltip"
                      triggers="manual"
                      (mouseleave)="onMouseLeave(t)"
                      (mouseenter)="onMouseEnter(t)"
                      container="body"
                      placement="top-start"
                      [attr.aria-label]="cell.ariaLabel"
                      class="html-cell"
                      [style.line-height]="cell.lineHeight"
                      [innerHTML]="cell.html"
                      [style.border-bottom]="cell.borderBottom"
                      [colSpan]="column.columnSpan ?? cell.columnSpan ?? 1"
                      >
                  </td>
                </ng-container>

                <!-- Text List Cell -->
                <ng-container *ngIf="cell.type === ZbpDataTable.TEXT_LIST_CELL">
                  <td id="{{column.key}}_{{i}}"
                      [attr.title]="cell.tooltip"
                      [attr.aria-label]="cell.ariaLabel"
                      class="text-list-cell"
                      [style.border-bottom]="cell.borderBottom"
                      [colSpan]="column.columnSpan ?? cell.columnSpan ?? 1">
                    <ul class="text-list">
                      <!-- has to be on single line to avoid adding extra spaces -->
                      <li *ngFor="let value of cell.textList" class="text-wrap">{{ value }}</li>
                    </ul>
                  </td>
                </ng-container>

                <!-- Simple List Cell -->
                <ng-container *ngIf="cell.type === ZbpDataTable.SIMPLE_LIST_CELL">
                  <td id="{{column.key}}_{{i}}"
                      class="simple-list-cell"
                      [style.text-align]="cell.textAlign"
                      [style.align-content]="cell.contentAlign"
                      [style.border-bottom]="cell.borderBottom"
                      [colSpan]="column.columnSpan ?? cell.columnSpan ?? 1">
                    <ul class="no-bullet-list">
                      @for (value of cell.itemList; track $index) {
                        <li class="simple-list">
                          @if (!value.onClick) {
                            <div class="text-wrap">
                              {{ value.text }}
                            </div>
                          } @else {
                            <zbp-button-component
                              [label]="value.text"
                              [tooltip]="value.tooltip"
                              [ariaLabel]="value.ariaLabel"
                              [theme]="cell.theme ?? 'primaryTextLink'"
                              (clickHandler)="value.onClick()"
                              fontSizeUnit="em"
                            ></zbp-button-component>
                          }
                        </li>
                      }
                    </ul>
                  </td>
                </ng-container>

                <!-- Link Cell -->
                <ng-container *ngIf="cell.type === ZbpDataTable.LINK_CELL">
                  <td id="{{column.key}}_{{i}}"
                    class="link-cell"
                    [colSpan]="column.columnSpan ?? cell.columnSpan ?? 1"
                    [style.text-align]="cell.textAlign"
                    [style.border-right]="cell.borderRight"
                    [style.border-bottom]="cell.borderBottom"
                  >
                    <zbp-button-component
                      #linkCellButton
                      [label]="cell.textTranslationKey ? (cell.textTranslationKey | translate) : cell.text"
                      [tooltip]="cell.tooltip"
                      [ariaLabel]="cell.ariaLabel"
                      [role]="cell.role ?? 'button'"
                      [theme]="cell.theme ?? 'primaryTextLink'"
                      (clickHandler)="cell.onClick(linkCellButton)"
                      fontSizeUnit="rem"
                    ></zbp-button-component>
                  </td>
                </ng-container>

                <!-- Audio Player Cell -->
                <ng-container *ngIf="cell.type === ZbpDataTable.AUDIO_PLAYER_CELL">
                  <td id="{{column.key}}_{{i}}"
                      class="audio-player-cell"
                      [style.border-bottom]="cell.borderBottom"
                      [colSpan]="column.columnSpan ?? cell.columnSpan ?? 1">
                    <zbp-audio-player
                      [playerTitle]="cell.playerTitle"
                      [assetName]="cell.assetName"
                      [audioFile]="cell.audioFile"
                    />
                  </td>
                </ng-container>

                <!-- Action Buttons Cell -->
                <ng-container *ngIf="cell.type === ZbpDataTable.ACTION_LIST_CELL">
                  <td id="{{column.key}}_{{i}}"
                      class="actions-cell"
                      [style.border-bottom]="cell.borderBottom"
                      [colSpan]="column.columnSpan ?? cell.columnSpan ?? 1">
                    <div class="actions-container">
                      <zbp-button-component
                        *ngFor="let action of cell.actions"
                        [iconName]="action.iconName"
                        [internalId]="action.internalId"
                        (clickHandler)="action.onClick()"
                        [tooltip]="action.tooltip"
                        [ariaLabel]="action.ariaLabel ?? action.tooltip"
                        [disabled]="action.disabled"
                        theme="icon"
                        iconSize="font-size"
                        fontSizeUnit="em"
                      ></zbp-button-component>
                    </div>
                  </td>
                </ng-container>

                <!-- Checkbox Cell -->
                <ng-container *ngIf="cell.type === ZbpDataTable.CHECKBOX_CELL">
                  <td id="{{column.key}}_{{i}}"
                      class="checkbox-cell"
                      [style.border-bottom]="cell.borderBottom"
                      [colSpan]="column.columnSpan ?? cell.columnSpan ?? 1">
                    <input
                      id="checkboxCell{{i}}"
                      type="checkbox"
                      #cb
                      (click)="checkboxCellClickHandler(cell)"
                      (change)="checkboxCellChangeHandler(column, cell)"
                      [formControlName]="cell.formControlName ?? column.key"
                      [attr.aria-label]="cell.ariaLabel ? cell.ariaLabel + ' ' + (i + 1) : 'checkbox ' + (i + 1)"
                      [ngbTooltip]="cb.disabled ? cell.disabledTooltip : ''"
                    />
                  </td>
                </ng-container>

                <!-- Radio Cell -->
                <ng-container *ngIf="cell.type === ZbpDataTable.RADIO_CELL">
                  <td class="radio-cell"
                      [style.border-bottom]="cell.borderBottom"
                      [colSpan]="column.columnSpan ?? cell.columnSpan ?? 1">
                    <input
                      id="radioCell{{i}}"
                      class="radioButton"
                      type="radio"
                      [tabindex]="cell.tabIndex ?? 0"
                      [value]="cell.radioValue"
                      [formControl]="getRadioControl(column)"
                      [attr.aria-label]="cell.ariaLabel ? cell.ariaLabel : 'radio button ' + (i + 1)"                    >
                  </td>
                </ng-container>

                <!-- Numeric Cell -->
                <ng-container *ngIf="cell.type === ZbpDataTable.NUMERIC_CELL">
                  <td id="{{column.key}}_{{i}}"
                      [style.border-bottom]="cell.borderBottom"
                      [colSpan]="column.columnSpan ?? cell.columnSpan ?? 1"
                      class="add-sub-cell">
                    <div>
                      <zbp-number-input
                        [innerFormControl]="cell.formControl"
                        [minValue]="0"
                        [maxValue]="cell.maxValue"
                        [showMax]="cell.showMax"
                        [constrainToLimits]="cell.constrainToLimits"
                        [placeholder]="cell.placeholder"
                        [allowMinusToNull]="cell.allowMinusToNull"
                        [allowNullValue]="cell.allowNullValue ?? false"
                        [applyConstraintOnNullInput]="cell.applyConstraintOnNullInput ?? true"
                        [counterAriaLabel]="cell.ariaLabel"
                        [subtractButtonAriaLabel]="cell.subtractButtonArialabel"
                        [addButtonAriaLabel]="cell.addButtonArialabel"
                      />
                    </div>
                  </td>
                </ng-container>

                <!-- BEHAVIOR_SUBJECT_CELL -->
                <ng-container *ngIf="cell.type === ZbpDataTable.BEHAVIOR_SUBJECT_CELL">
                  <td id="{{column.key}}_{{i}}"
                      class="behavior-subject-cell"
                      [attr.title]="cell.tooltip"
                      [attr.aria-label]="cell.ariaLabel"
                      [style.border-bottom]="cell.borderBottom"
                      [colSpan]="column.columnSpan ?? cell.columnSpan ?? 1">
                    {{ cell.value?.value }}
                  </td>
                </ng-container>

                <!-- Score Benchmark Cell -->
                <ng-container *ngIf="cell.type === ZbpDataTable.SCORE_BENCHMARK_CELL">
                  <td id="{{column.key}}_{{i}}"
                      [attr.title]="cell.tooltip"
                      [attr.aria-label]="cell.ariaLabel"
                      class="score-benchmark-cell"
                      [style.border-bottom]="cell.borderBottom"
                      [colSpan]="column.columnSpan ?? cell.columnSpan ?? 1"
                  >
                    <div [ngClass]="['score-benchmark-text', 'score-benchmark-' + getBenchmarkClass(cell.scoreBenchmark)]">
                      {{ getBenchmarkLabelTranslationKey(cell.scoreBenchmark) | translate }}
                    </div>
                  </td>
                </ng-container>

                <!-- Collapsible Cell -->
                <ng-container *ngIf="cell.type === ZbpDataTable.COLLAPSIBLE_CELL">
                  <td id="{{column.key}}_{{i}}"
                      [attr.title]="cell.tooltip"
                      [attr.aria-label]="cell.ariaLabel"
                      class="collapsible-cell"
                      [style.border-bottom]="cell.borderBottom"
                      [colSpan]="column.columnSpan ?? cell.columnSpan ?? 1"
                  >
                    <div class="collapsible-cell-content"
                         [class.has-leading-content]="!!cell.textList?.length">
                    @if (cell.textList?.length) {
                      <ul class="no-bullet-list">
                        <!-- has to be on single line to avoid adding extra spaces -->
                        <li *ngFor="let value of cell.textList" class="text-wrap">{{ value }}</li>
                      </ul>
                    }
                    <zbp-button-component
                      [iconName]="getCollapsibleRow(collapsibleRowId)?.classList?.contains('show') ? zbpIconNames.arrow_chevron_up : zbpIconNames.arrow_chevron_down"
                      class="ms-auto"
                      theme="icon"
                      iconSize="font-size"
                      (click)="toggleCollapsible(collapsibleRowId)"
                      [attr.aria-expanded]="getCollapsibleRow(collapsibleRowId)?.classList?.contains('show') ? true : false"
                      [ariaLabel]="getCollapsibleRow(collapsibleRowId)?.classList?.contains('show') ? 'collapse additional details' : 'expand for additional details'"
                    />
                    </div>
                  </td>
                </ng-container>

                <!-- SVG Cell -->
                <ng-container *ngIf="cell.type === ZbpDataTable.ICON_CELL">
                  <td id="{{column.key}}_{{i}}"
                      [attr.title]="cell.tooltip"
                      class="score-benchmark-cell"
                      [style.border-bottom]="cell.borderBottom"
                      [colSpan]="column.columnSpan ?? cell.columnSpan ?? 1"
                  >
                    <zbp-icon
                      [iconName]="cell.iconName"
                      [iconSize]="cell.iconSize ?? 'default'"
                      [primaryColor]="cell.primaryColor"
                      [secondaryColor]="cell.secondaryColor"
                      [attr.aria-label]="cell.arialLabelTranslationKey
                                          ? (cell.arialLabelTranslationKey | translate)
                                          : cell.ariaLabel"
                    />
                  </td>
                </ng-container>

                <!-- Pill Cell -->
                <ng-container *ngIf="cell.type === ZbpDataTable.PILL_CELL">
                  <td id="{{column.key}}_{{i}}"
                      [attr.title]="cell.tooltip"
                      [attr.aria-label]="cell.ariaLabel"
                      class="pill-cell"
                      [colSpan]="column.columnSpan ?? cell.columnSpan ?? 1"
                      [style.justify-items]="cell.justifyItems"
                      [style.border-bottom]="cell.borderBottom"
                      [style.backgroundColor]="cell.cellBackgroundColor"
                  >
                    @if (cell.onClick) {
                      <button
                        class="pill-cell-text d-block"
                        [style.color]="cell.textColor"
                        [style.backgroundColor]="cell.backgroundColor"
                        [style.borderColor]="cell.borderColor"
                        [style.width]="cell.pillWidth"
                        [style.line-height]="cell.lineHeight"
                        (click)="cell.onClick()">
                        {{ cell.text }}
                        <zbp-icon
                          *ngIf="!!cell.iconName"
                          [iconName]="cell.iconName"
                          [iconSize]="'font-size'"
                          [primaryColor]="cell.textColor"
                        />
                      </button>
                    } @else {
                      <div
                        class="pill-cell-text no-click"
                        [style.color]="cell.textColor"
                        [style.backgroundColor]="cell.backgroundColor"
                        [style.width]="cell.pillWidth"
                        [style.line-height]="cell.lineHeight"
                        [style.borderColor]="cell.borderColor">
                        {{ cell.text }}
                        <zbp-icon
                        *ngIf="!!cell.iconName"
                          [iconName]="cell.iconName"
                          [iconSize]="'font-size'"
                          [primaryColor]="cell.textColor"
                        />
                      </div>
                    }
                  </td>
                </ng-container>

              </ng-container>
            </ng-container>
          </tr>
          <!-- Collapsible rows can be broken on print -->
          <tr [id]="collapsibleRowId"
            *ngIf="hasCollapsibleCell(row)"
            class="collapsible-row collapse">
            <!-- for each row loop over the columns -->
            <ng-container *ngFor="let column of columns">

              <!-- get cell from the row based on current column -->
              @let cell = getCell(row, column);
                @if (cell.type === ZbpDataTable.COLLAPSIBLE_CELL) {
                  <td
                    class="collapsible-table-cell"
                    [attr.colspan]="columnLength"
                  >
                    <!-- Loop over each item in content -->
                    @for (item of cell.content; track $index) {

                      @switch (item.type) {
                        @case (ZbpDataTable.COLLAPSIBLE_TABLE_TYPE){
                          <div>
                            <zbp-data-table
                              [columns]="item.columns"
                              [rows]="item.rows"
                              [uniqueIdentifierPrefix]="collapsibleRowId + '_' + $index + '_'"
                              [colorPrint]="true"
                            />
                          </div>
                        }
                        @case (ZbpDataTable.COLLAPSIBLE_IMAGE_TYPE) {
                          <div class="collapsible-image-wrapper">
                            <img [src]="item.src" class="collapsible-image"/>
                          </div>
                        }
                        <!-- Just a row, not a nested table. If list is empty, displays a message (when provided) or dash.-->
                        @case (ZbpDataTable.COLLAPSIBLE_DEFINITION_LIST_TYPE) {
                          @if (item?.items?.length) {
                            <dl class="collapsible-definition-list">
                              @for (definition of item.items; track $index) {
                                <dt>{{ definition.term }}</dt>
                                <dd>{{ definition.description }}</dd>
                              }
                            </dl>
                          } @else {
                            @if(item.definitionNotAvailableTranslationKey) {
                              <em>{{ item.definitionNotAvailableTranslationKey | translate }}</em>
                            } @else {
                              <span>-</span> 
                            }
                          }
                        }
                      }
                    }
                  </td>
                }
            </ng-container>
          </tr>
        </ng-container>
      </ng-container>
    </ng-container>
    </tbody>
  </table>

  <ng-template #spinnerTemplate>
    <tr>
      <td [colSpan]="columnLength">
        <zbp-preloader></zbp-preloader>
      </td>
    </tr>
  </ng-template>

  <ng-template #noResultsTemplate>
    <tr>
      <!-- single line to prevent extra whitespace characters in output -->
      <td id="noResultsMessage" [colSpan]="columnLength" class="table-body-message">
        <ng-container *ngIf="!noResultsCustomTemplate; else noResultsCustomTemplate">
          {{ noResultsMessage }}
        </ng-container>
      </td>
    </tr>
  </ng-template>
</div>
<div *ngIf="leftFooterContent || rightFooterContent || collectionSize > pageSize"
     class="styled-table-footer-container">
  <div class="styled-table-footer-left">
    <ng-container *ngTemplateOutlet="leftFooterContent"></ng-container>
  </div>
  <div class="styled-table-footer-right">
    <ng-container *ngTemplateOutlet="rightFooterContent"></ng-container>
    <nav *ngIf="collectionSize > pageSize"
         class="styled-pagination">
      <ngb-pagination #pagination
                      [pageSize]="pageSize"
                      [maxSize]="maxPageCount"
                      [collectionSize]="collectionSize"
                      [page]="page"
                      (pageChange)="pageChangeWrapper($event)"
      ></ngb-pagination>
    </nav>
  </div>
</div>
